import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useTranslatedToast } from '@/hooks/useTranslatedToast';
import { useTranslation } from '@/hooks/useTranslation';

export type NotificationCategory = 'urgent_action' | 'milestone' | 'reminder' | 'invitation';

export interface NotificationCategoryPreferences {
  category: NotificationCategory;
  email_enabled: boolean;
  in_app_enabled: boolean;
  batch_enabled: boolean;
  batch_frequency_hours: number;
  quiet_hours_start: string;
  quiet_hours_end: string;
  timezone: string;
}

export interface NotificationEngagementData {
  total_sent: number;
  email_opened: number;
  email_clicked: number;
  action_taken: number;
  engagement_rate: number;
  top_notification_types: Array<{
    notification_type: string;
    count: number;
    engagement_rate: number;
  }>;
}

export const useNotificationPreferences = () => {
  const toast = useTranslatedToast();
  const { t } = useTranslation('ui');
  const [preferences, setPreferences] = useState<NotificationCategoryPreferences[]>([]);
  const [loading, setLoading] = useState(true);
  const [engagementData, setEngagementData] = useState<NotificationEngagementData | null>(null);

  // Default preferences for each category
  const getDefaultPreferences = useCallback((category: NotificationCategory): Omit<NotificationCategoryPreferences, 'category'> => {
    switch (category) {
      case 'urgent_action':
        return {
          email_enabled: true,
          in_app_enabled: true,
          batch_enabled: false, // Never batch urgent actions
          batch_frequency_hours: 0,
          quiet_hours_start: '22:00',
          quiet_hours_end: '08:00',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        };
      case 'invitation':
        return {
          email_enabled: true,
          in_app_enabled: true,
          batch_enabled: false, // Never batch invitations
          batch_frequency_hours: 0,
          quiet_hours_start: '22:00',
          quiet_hours_end: '08:00',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        };
      case 'milestone':
        return {
          email_enabled: true,
          in_app_enabled: true,
          batch_enabled: true, // Can batch milestones
          batch_frequency_hours: 4,
          quiet_hours_start: '22:00',
          quiet_hours_end: '08:00',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        };
      case 'reminder':
        return {
          email_enabled: true,
          in_app_enabled: true,
          batch_enabled: true, // Can batch reminders
          batch_frequency_hours: 24,
          quiet_hours_start: '22:00',
          quiet_hours_end: '08:00',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        };
    }
  }, []);

  const loadPreferences = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('user_notification_categories')
        .select('*')
        .order('category');

      if (error) throw error;

      // Ensure all categories have preferences (create defaults if missing)
      const categories: NotificationCategory[] = ['urgent_action', 'milestone', 'reminder', 'invitation'];
      const existingCategories = ((data as any) || []).map((p: any) => p.category);
      const missingCategories = categories.filter(cat => !existingCategories.includes(cat));

      // Create default preferences for missing categories
      const defaultPrefs: NotificationCategoryPreferences[] = missingCategories.map(category => ({
        category,
        ...getDefaultPreferences(category)
      }));

      // Combine existing and default preferences
      const allPreferences = [
        ...((data as any) || []),
        ...defaultPrefs
      ].sort((a, b) => {
        const order = ['urgent_action', 'invitation', 'milestone', 'reminder'];
        return order.indexOf((a as any).category) - order.indexOf((b as any).category);
      });

      setPreferences(allPreferences as any);
    } catch (error) {
      console.error('Failed to load notification preferences:', error);
      toast.raw.error("Failed to load your notification preferences. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [getDefaultPreferences]);

  const updatePreference = useCallback(async (
    category: NotificationCategory, 
    updates: Partial<Omit<NotificationCategoryPreferences, 'category'>>
  ) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('user_notification_categories')
        .upsert({
          user_id: userData.user.id,
          category,
          ...updates
        } as any);

      if (error) throw error;

      // Update local state
      setPreferences(prev => 
        prev.map(pref => 
          pref.category === category 
            ? { ...pref, ...updates }
            : pref
        )
      );

      toast.raw.success(`Your ${category.replace('_', ' ')} notification preferences have been saved.`);
    } catch (error) {
      console.error('Failed to update notification preference:', error);
      toast.raw.error("Failed to save your notification preferences. Please try again.");
    }
  }, []);

  const loadEngagementData = useCallback(async () => {
    try {
      // Get engagement statistics
      const { data: engagementStats, error } = await supabase
        .from('notification_engagement_tracking')
        .select(`
          notification_type,
          action_taken,
          email_opened,
          email_clicked,
          created_at
        `)
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id as any)
        .gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()); // Last 30 days

      if (error) throw error;

      if (engagementStats && engagementStats.length > 0) {
        const totalSent = engagementStats.length;
        const emailOpened = engagementStats.filter((s: any) => (s as any).email_opened).length;
        const emailClicked = engagementStats.filter((s: any) => (s as any).email_clicked).length;
        const actionTaken = engagementStats.filter((s: any) => (s as any).action_taken === 'clicked').length;

        // Group by notification type
        const typeStats = engagementStats.reduce((acc: any, stat) => {
          if (!acc[(stat as any).notification_type]) {
            acc[(stat as any).notification_type] = { count: 0, engaged: 0 };
          }
          acc[(stat as any).notification_type].count++;
          if ((stat as any).action_taken === 'clicked' || (stat as any).email_clicked) {
            acc[(stat as any).notification_type].engaged++;
          }
          return acc;
        }, {});

        const topNotificationTypes = Object.entries(typeStats)
          .map(([type, stats]: [string, any]) => ({
            notification_type: type,
            count: stats.count,
            engagement_rate: stats.count > 0 ? (stats.engaged / stats.count) * 100 : 0
          }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 5);

        setEngagementData({
          total_sent: totalSent,
          email_opened: emailOpened,
          email_clicked: emailClicked,
          action_taken: actionTaken,
          engagement_rate: totalSent > 0 ? (actionTaken / totalSent) * 100 : 0,
          top_notification_types: topNotificationTypes
        });
      }
    } catch (error) {
      console.error('Failed to load engagement data:', error);
    }
  }, []);

  const getCategoryLabel = useCallback((category: NotificationCategory): string => {
    switch (category) {
      case 'urgent_action': return t('settings.urgent_actions');
      case 'milestone': return t('settings.milestones');
      case 'reminder': return t('settings.reminders');
      case 'invitation': return t('settings.invitations');
    }
  }, [t]);

  const getCategoryDescription = useCallback((category: NotificationCategory): string => {
    switch (category) {
      case 'urgent_action': return t('settings.urgent_actions_description');
      case 'milestone': return t('settings.milestones_description');
      case 'reminder': return t('settings.reminders_description');
      case 'invitation': return t('settings.invitations_description');
    }
  }, [t]);

  useEffect(() => {
    loadPreferences();
    loadEngagementData();
  }, [loadPreferences, loadEngagementData]);

  return {
    preferences,
    loading,
    engagementData,
    updatePreference,
    loadPreferences,
    loadEngagementData,
    getCategoryLabel,
    getCategoryDescription
  };
};